import { useState, useEffect, useMemo } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, TrendingUp, Building2 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceDot,
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';

interface MonthlyAgencyData {
  month: string;
  monthLabel: string;
  [key: string]: number | string; // Dynamic agency keys
}

interface AgencyInfo {
  id: string;
  code: string;
  name: string;
}

interface CumulativeBillableHoursChartProps {
  agencyId?: string | null;
  agencyName?: string | null;
  startDate?: Date;
  endDate?: Date;
}

import { RECHARTS_COLORS } from '@/constants/chartColors';

// Agency color palette using Recharts default colors
const AGENCY_PALETTE: Record<string, string> = {
  'CFHH': RECHARTS_COLORS[0],  // Purple
  'OHHC': RECHARTS_COLORS[1],  // Green
  'BSHC': RECHARTS_COLORS[2],  // Yellow
  'FN': RECHARTS_COLORS[3],    // Orange
  'CC': RECHARTS_COLORS[7],    // Blue
};

export default function CumulativeBillableHoursChart({ agencyId, agencyName, startDate, endDate }: CumulativeBillableHoursChartProps) {
  const chartColors = useChartColors();
  const isAllAgencies = !agencyId;
  const [data, setData] = useState<MonthlyAgencyData[]>([]);
  const [agencies, setAgencies] = useState<AgencyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState<Record<string, number>>({});

  useEffect(() => {
    loadData();
  }, [agencyId, startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get all approved agencies
      const { data: agenciesData, error: agenciesError } = await supabase
        .from('agencies')
        .select('id, code, name')
        .eq('is_approved', true);

      if (agenciesError) throw agenciesError;

      // Map agencies with short codes
      const agencyList: AgencyInfo[] = (agenciesData || []).map(a => ({
        id: a.id,
        code: a.code.split('-')[0], // Get short code (e.g., "CFHH" from "CFHH-01")
        name: a.name
      }));

      setAgencies(agencyList);

      // Generate months from Jan 2025 to now
      const months = eachMonthOfInterval({
        start: new Date('2025-01-01'),
        end: new Date()
      });

      const monthlyData: MonthlyAgencyData[] = [];
      const cumulativeTotals: Record<string, number> = {};
      
      // Initialize cumulative totals
      agencyList.forEach(a => {
        cumulativeTotals[a.code] = 0;
      });

      for (const monthDate of months) {
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        const monthKey = format(monthDate, 'yyyy-MM');
        const monthLabel = format(monthDate, 'MMM');

        // Query sessions for this month that diverted from ER
        let query = supabase
          .from('triage_sessions')
          .select(`
            id,
            agency_id,
            recommendation,
            patients!inner (
              hospital_er_hours
            )
          `)
          .gte('completed_at', monthStart.toISOString())
          .lte('completed_at', monthEnd.toISOString())
          .not('completed_at', 'is', null)
          .in('recommendation', ['HOME', 'PCP', 'URGENT_CARE']);

        if (agencyId) {
          query = query.eq('agency_id', agencyId);
        }

        const { data: sessions, error } = await query;

        if (error) {
          console.error('Error loading sessions:', error);
          continue;
        }

        // Calculate hours saved per agency
        const monthlyHours: Record<string, number> = {};
        agencyList.forEach(a => {
          monthlyHours[a.id] = 0;
        });

        if (sessions) {
          for (const session of sessions) {
            const patient = session.patients as any;
            const erHours = patient?.hospital_er_hours || 8;
            if (session.agency_id && monthlyHours[session.agency_id] !== undefined) {
              monthlyHours[session.agency_id] += erHours;
            }
          }
        }

        // Create month data point with cumulative values
        const monthData: MonthlyAgencyData = {
          month: monthKey,
          monthLabel
        };

        agencyList.forEach(a => {
          cumulativeTotals[a.code] += monthlyHours[a.id] || 0;
          monthData[a.code] = Math.round(cumulativeTotals[a.code]);
        });

        monthlyData.push(monthData);
      }

      setData(monthlyData);
      setTotals(cumulativeTotals);
    } catch (error) {
      console.error('Error loading billable hours data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (hours: number): string => {
    if (hours >= 1000) {
      return `${(hours / 1000).toFixed(1)}k`;
    }
    return hours.toLocaleString();
  };

  // Get agencies sorted by the R order
  const sortedAgencies = useMemo(() => {
    const order = ['CFHH', 'OHHC', 'BSHC', 'FN', 'CC'];
    return agencies
      .filter(a => (totals[a.code] || 0) > 0)
      .sort((a, b) => {
        const aIdx = order.indexOf(a.code);
        const bIdx = order.indexOf(b.code);
        if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
        if (aIdx >= 0) return -1;
        if (bIdx >= 0) return 1;
        return (totals[b.code] || 0) - (totals[a.code] || 0);
      });
  }, [agencies, totals]);

  // Get end points for labels
  const endPoints = useMemo(() => {
    if (data.length === 0) return [];
    const lastMonth = data[data.length - 1];
    return sortedAgencies.map(a => ({
      code: a.code,
      name: a.name,
      value: lastMonth[a.code] as number || 0,
      color: AGENCY_PALETTE[a.code] || '#888888'
    }));
  }, [data, sortedAgencies]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div 
        className="rounded-lg shadow-lg p-3 text-sm border bg-white"
        style={chartColors.tooltipStyle}
      >
        <p className="font-semibold mb-2 text-gray-900">{label} 2025</p>
        {payload
          .sort((a: any, b: any) => b.value - a.value)
          .map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600">{entry.name}:</span>
            </div>
            <span className="font-medium text-gray-900">
              {Number(entry.value).toLocaleString()} hrs
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Custom dot for end points
  const renderEndPointDot = (props: any, agencyCode: string) => {
    const { cx, cy, index } = props;
    if (index !== data.length - 1) return null;
    
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill={AGENCY_PALETTE[agencyCode] || '#888888'}
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName}`}>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const totalHoursSaved = Object.values(totals).reduce((sum, val) => sum + val, 0);

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <Clock className="h-5 w-5 text-blue-700" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
                Cumulative Billable Hours Saved (2025)
              </CardTitle>
              <CardDescription className="text-gray-600">
                Hours preserved by diverting patients from ER visits
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1 bg-white border-gray-300 text-gray-700">
              <Building2 className="h-3 w-3" />
              {isAllAgencies ? 'All Agencies' : agencyName || 'Agency'}
            </Badge>
            <Badge variant="outline" className="gap-1 bg-white border-gray-300 text-gray-700">
              <TrendingUp className="h-3 w-3" />
              {formatHours(totalHoursSaved)} total hours
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-[400px] text-gray-500">
            No data available for the selected period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart 
              data={data} 
              margin={{ top: 20, right: 100, left: 60, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="0" stroke={chartColors.grid} />
              <XAxis
                dataKey="monthLabel"
                tick={{ fontSize: 11, fontFamily: 'Georgia, serif', fill: chartColors.axis }}
                tickLine={false}
                axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
              />
              <YAxis
                tick={{ fontSize: 11, fontFamily: 'Georgia, serif', fill: chartColors.axis }}
                tickLine={false}
                axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
                tickFormatter={(value) => value.toLocaleString()}
                label={{
                  value: 'Cumulative billable hours',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 0,
                  style: { 
                    textAnchor: 'middle', 
                    fontSize: 11, 
                    fontFamily: 'Georgia, serif',
                    fontWeight: 'bold',
                    fill: chartColors.axis
                  }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ 
                  paddingBottom: 10,
                  paddingLeft: 60,
                  fontFamily: 'Georgia, serif',
                  fontSize: 11
                }}
                formatter={(value) => (
                  <span style={{ color: '#374151' }}>{value}</span>
                )}
              />
              
              {sortedAgencies.map((agency, index) => (
                <Line
                  key={agency.code}
                  type="monotone"
                  dataKey={agency.code}
                  name={agency.code}
                  stroke={AGENCY_PALETTE[agency.code] || '#888888'}
                  strokeWidth={3}
                  dot={{ fill: AGENCY_PALETTE[agency.code] || '#888888', strokeWidth: 2, stroke: '#fff', r: 3 }}
                  activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff', fill: AGENCY_PALETTE[agency.code] || '#888888' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                  animationBegin={index * 200}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* End point labels - positioned to the right */}
        {data.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-gray-200">
            {endPoints
              .sort((a, b) => b.value - a.value)
              .map((ep) => (
              <div key={ep.code} className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: `${ep.color}10` }}>
                <span 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: ep.color }}
                />
                <span className="text-xs font-medium text-gray-700">{ep.code}:</span>
                <span className="text-sm font-bold" style={{ color: ep.color }}>
                  {ep.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-border">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">
              {formatHours(totalHoursSaved)}
            </p>
            <p className="text-xs text-muted-foreground">Total Hours Saved</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">
              {formatHours(totalHoursSaved / Math.max(data.length, 1))}
            </p>
            <p className="text-xs text-muted-foreground">Avg Monthly Hours</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">
              {sortedAgencies.length}
            </p>
            <p className="text-xs text-muted-foreground">Active Agencies</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}